// src/pages/HomePage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { Card, CardHeader, CardBody, Button, FormControl, ModalShell } from '../components/ui';
import { fetchRequestsSummary } from '../services/comersRequestFlow';
import { REQUEST_STATUS_PRESETS, getRequestPresetRoute } from '../config/requestPresets';
import '../styles/home.css';

const MySwal = withReactContent(Swal);

const FEATURED_ACTION_ID = 'requests';

const REQUEST_STEPS = [
  { id: 'start', label: 'Nueva solicitud', icon: 'bi-play-circle' },
  { id: 'offer', label: 'Oferta', icon: 'bi-file-earmark-text' },
  { id: 'data', label: 'Datos personales', icon: 'bi-person' },
  { id: 'docs', label: 'Documentos', icon: 'bi-upload' },
  { id: 'pre', label: 'Pre-Confirmación', icon: 'bi-shield-check' }
];

const showOriginationsBoardAccess = process.env.REACT_APP_ENABLE_ORIGINATIONS_BOARD === 'true';

function HomePage() {
  const navigate = useNavigate();
  useAuth();
  const [showDniModal, setShowDniModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [dni, setDni] = useState('');
  const [requestsSummary, setRequestsSummary] = useState({});
  const [summaryLoading, setSummaryLoading] = useState(false);

  const mainActions = [
    {
      id: 'requests',
      title: 'Nueva solicitud',
      description: 'Iniciar una solicitud de crédito en efectivo',
      icon: 'bi-cart-plus',
      color: 'primary',
      route: '/solicitudes',
      badge: null,
      requiresDni: true,
      action: 'request'
    },
    ...(showOriginationsBoardAccess ? [{
      id: 'originationsBoard',
      title: 'Tablero de originación',
      description: 'Ver solicitudes por etapa',
      icon: 'bi-kanban',
      color: 'primary',
      route: '/tablero-originacion',
      badge: 'Beta',
      requiresDni: false,
      action: null
    }] : []),
    {
      id: 'viewRequests',
      title: 'Ver Créditos',
      description: 'Consultar operaciones y estados vigentes',
      icon: 'bi-credit-card-2-front',
      color: 'primary',
      route: '/creditos',
      badge: null,
      requiresDni: false,
      action: null
    },
    {
      id: 'approvedRequests',
      title: 'Solicitudes aprobadas',
      description: 'Continuar documentación, medio de cobro y firma',
      icon: 'bi-patch-check',
      color: 'success',
      route: getRequestPresetRoute(REQUEST_STATUS_PRESETS.approved.key),
      badge: null,
      requiresDni: false,
      action: null
    },
    {
      id: 'observedRequests',
      title: 'Solicitudes observadas',
      description: 'Revisar observaciones pendientes de resolución',
      icon: 'bi-exclamation-circle',
      color: 'warning',
      route: getRequestPresetRoute(REQUEST_STATUS_PRESETS.observed.key),
      badge: null,
      notificationCount: null,
      requiresDni: false,
      action: null
    },
    {
      id: 'rejectedRequests',
      title: 'Solicitudes rechazadas',
      description: 'Consultar rechazos, anulaciones y desistimientos',
      icon: 'bi-x-circle',
      color: 'danger',
      route: getRequestPresetRoute(REQUEST_STATUS_PRESETS.rejected.key),
      badge: null,
      notificationCount: null,
      requiresDni: false,
      action: null
    },
    {
      id: 'waitingSettlementRequests',
      title: 'Esperando liquidación',
      description: 'Ver solicitudes en análisis de pago o para liquidar',
      icon: 'bi-hourglass-split',
      color: 'info',
      route: getRequestPresetRoute(REQUEST_STATUS_PRESETS.waitingSettlement.key),
      badge: null,
      notificationCount: null,
      requiresDni: false,
      action: null
    },
    {
      id: 'riskAnalysisRequests',
      title: 'En análisis de riesgo',
      description: 'Ver solicitudes pendientes de revisión por la financiera',
      icon: 'bi-shield-check',
      color: 'secondary',
      route: getRequestPresetRoute(REQUEST_STATUS_PRESETS.riskAnalysis.key),
      badge: null,
      notificationCount: null,
      requiresDni: false,
      action: null
    },
    {
      id: 'collections',
      title: 'Cobrar una cuota',
      description: 'Registrar pagos del cliente sobre créditos vigentes',
      icon: 'bi-cash-coin',
      color: 'success',
      route: '/cobros',
      badge: process.env.REACT_APP_ALLOW_COLLECTION_MANAGEMENT === 'true' ? null : 'Próximamente',
      requiresDni: true,
      action: 'collection'
    },
    {
      id: 'zreports',
      title: 'Cierre Z',
      description: 'Generar reportes de cierre operativo',
      icon: 'bi-receipt',
      color: 'warning',
      route: '/reportes-z',
      badge: process.env.REACT_APP_ENABLE_ZREPORT_MANAGEMENT === 'true' ? null : 'Próximamente',
      requiresDni: false,
      action: null
    },
    {
      id: 'observations',
      title: 'Observaciones',
      description: 'Gestionar alertas y observaciones del circuito',
      icon: 'bi-check-circle',
      color: 'info',
      route: '/observaciones',
      badge: process.env.REACT_APP_ENABLE_OBSERVATIONS_MANAGEMENT === 'true' ? null : 'Próximamente',
      requiresDni: false,
      action: null
    }
  ];

  const quickActions = mainActions.filter(
    (a) => a.id !== FEATURED_ACTION_ID && a.badge !== 'Próximamente'
  );

  const loadRequestsSummary = async () => {
    setSummaryLoading(true);
    try {
      const summary = await fetchRequestsSummary();
      setRequestsSummary(summary);
    } catch (error) {
      console.error('Error loading requests summary:', error);
      setRequestsSummary({});
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    loadRequestsSummary();
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showDniModal) closeDniModal();
    };
    if (showDniModal) document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showDniModal]);

  const getQuickActionBadgeCount = (action) => {
    if (typeof action.notificationCount === 'number') {
      return action.notificationCount > 0 ? action.notificationCount : null;
    }

    const summaryMap = {
      approvedRequests: requestsSummary.approved?.total,
      observedRequests: requestsSummary.observed?.total,
      rejectedRequests: requestsSummary.rejected?.unseen,
      waitingSettlementRequests: requestsSummary.waitingSettlement?.total,
      riskAnalysisRequests: requestsSummary.riskAnalysis?.total
    };

    const count = summaryMap[action.id];
    if (typeof count === 'number') return count > 0 ? count : null;

    return null;
  };

  const handleActionClick = (action) => {
    if (action.requiresDni) {
      setPendingAction(action);
      setShowDniModal(true);
    } else {
      navigate(action.route);
    }
  };

  const handleDniSubmit = () => {
    if (!dni || dni.length < 7 || dni.length > 8) {
      MySwal.fire({
        icon: 'warning',
        title: 'DNI inválido',
        text: 'El DNI debe tener entre 7 y 8 caracteres.',
      });
      return;
    }
    navigate(`/clientes/${dni}`, { state: { pendingAction: pendingAction?.action || 'request' } });
    setShowDniModal(false);
    setPendingAction(null);
    setDni('');
  };

  const handleDniChange = (e) => {
    const value = e.target.value;
    if (value === '' || (/^\d+$/.test(value) && value.length <= 8)) setDni(value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleDniSubmit();
  };

  const closeDniModal = () => {
    setShowDniModal(false);
    setPendingAction(null);
    setDni('');
  };

  const submitFeaturedDni = () => {
    if (!dni || dni.length < 7 || dni.length > 8) {
      MySwal.fire({
        icon: 'warning',
        title: 'DNI inválido',
        text: 'El DNI debe tener entre 7 y 8 caracteres.',
      });
      return;
    }
    navigate(`/clientes/${dni}`, { state: { pendingAction: 'request' } });
    setDni('');
  };

  const requestsSummaryBadge =
    summaryLoading && Object.keys(requestsSummary).length === 0 ? (
      <span className="home-page__quick-notification home-page__quick-notification--loading" aria-hidden />
    ) : null;

  return (
    <div className="page-content home-page rg-home rg-shell-content">
      <div className="home-page__grid">
        {/* Sidebar: Acciones rápidas */}
        <aside className="home-page__sidebar">
          <Card className="home-page__sidebar-card">
            <CardHeader className="home-page__sidebar-header">
              <div className="home-page__sidebar-header-row">
                <div className="home-page__sidebar-heading-copy">
                  <h2 className="home-page__sidebar-title"><i className="bi bi-lightning-charge-fill" aria-hidden /> Acciones rápidas</h2>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="home-page__sidebar-refresh"
                  disabled={summaryLoading}
                  onClick={loadRequestsSummary}
                  aria-label="Actualizar contadores de solicitudes"
                  title="Actualizar contadores"
                >
                  <i className={`bi bi-arrow-clockwise${summaryLoading ? ' rg-spin' : ''}`} aria-hidden />
                </Button>
              </div>
            </CardHeader>
            <CardBody className="home-page__sidebar-body">
              <ul className="home-page__quick-list">
                {quickActions.map((action) => (
                  <li key={action.id}>
                    <button
                      type="button"
                      className="home-page__quick-item rg-focusable"
                      onClick={() => handleActionClick(action)}
                    >
                      <span className="home-page__quick-icon">
                        <i className={`bi ${action.icon}`} aria-hidden />
                      </span>
                      <span className="home-page__quick-text">
                        <span className="home-page__quick-label-row">
                          <span className="home-page__quick-label">{action.title}</span>
                          {['approvedRequests', 'observedRequests', 'rejectedRequests', 'waitingSettlementRequests', 'riskAnalysisRequests'].includes(action.id) && requestsSummaryBadge}
                          {action.badge && action.badge !== 'Próximamente' && (
                            <span className="home-page__quick-tag">{action.badge}</span>
                          )}
                          {typeof getQuickActionBadgeCount(action) === 'number' && (
                            <span
                              className="home-page__quick-notification"
                              aria-label={
                                action.id === 'rejectedRequests'
                                  ? `${getQuickActionBadgeCount(action)} solicitudes rechazadas sin revisar`
                                  : action.id === 'approvedRequests'
                                    ? `${getQuickActionBadgeCount(action)} solicitudes aprobadas pendientes`
                                  : `${getQuickActionBadgeCount(action)} notificaciones`
                              }
                            >
                              {getQuickActionBadgeCount(action)}
                            </span>
                          )}
                        </span>
                        <span className="home-page__quick-desc">{action.description}</span>
                      </span>
                      <i className="bi bi-chevron-right home-page__quick-chevron" aria-hidden />
                    </button>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        </aside>

        {/* Main: Nueva solicitud */}
        <main className="home-page__main">
          <Card className="home-page__featured-card">
            <CardBody className="home-page__featured-body">
              <div className="home-page__featured-icon">
                <i className="bi bi-file-earmark-text" aria-hidden />
              </div>
              <h3 className="home-page__featured-title">Nueva solicitud</h3>

              <div className="home-page__featured-form rg-spacing-stack">
                <div className="rg-dni-surface">
                  <div className="home-page__featured-row rg-dni-group">
                    <FormControl
                      id="home-dni"
                      placeholder="Ej: 30.123.456"
                      value={dni}
                      onChange={handleDniChange}
                      onKeyDown={(e) => e.key === 'Enter' && submitFeaturedDni()}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      iconLeft={<i className="bi bi-person-vcard" />}
                      className="home-page__featured-field"
                    />
                    <Button
                      variant="primary"
                      onClick={submitFeaturedDni}
                      disabled={!dni || dni.length < 7}
                      className="home-page__featured-btn"
                    >
                      <i className="bi bi-search" aria-hidden />
                      Buscar Cliente
                    </Button>
                  </div>
                </div>
              </div>

              <p className="home-page__featured-hint">
                También podés usar este acceso para continuar solicitudes aprobadas o registrar cobranzas desde el perfil.
              </p>

              <div className="home-page__stepper" role="navigation" aria-label="Pasos de la solicitud">
                {REQUEST_STEPS.map((step, index) => (
                  <React.Fragment key={step.id}>
                    <span className="home-page__stepper-step">
                      <i className={`bi ${step.icon}`} aria-hidden />
                      <span>{step.label}</span>
                    </span>
                    {index < REQUEST_STEPS.length - 1 && (
                      <i className="bi bi-chevron-right home-page__stepper-sep" aria-hidden />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </CardBody>
          </Card>
        </main>
      </div>

      {/* Modal DNI para acciones que lo requieren (ej. Cobrar cuota) */}
      {showDniModal && (
        <ModalShell
          show={showDniModal}
          onHide={closeDniModal}
          title="Buscar cliente"
          size="md"
          className="rg-modal--seller home-page__modal"
          backdropClassName="rg-modal-backdrop--seller"
          footer={(
            <div className="home-page__modal-actions">
              <Button variant="secondary" onClick={closeDniModal}>Cancelar</Button>
              <Button
                variant="primary"
                onClick={handleDniSubmit}
                disabled={!dni || dni.length < 7}
              >
                <i className="bi bi-search" aria-hidden />
                Buscar cliente
              </Button>
            </div>
          )}
        >
          <div className="home-page__modal-copy">
            <p className="home-page__modal-lead">
              Ingresá el DNI del titular para abrir su perfil y continuar con la acción seleccionada.
            </p>
            <FormControl
              id="dni-modal"
              label="DNI del cliente"
              placeholder="Ej: 30123456"
              value={dni}
              onChange={handleDniChange}
              onKeyDown={handleKeyDown}
              inputMode="numeric"
              pattern="[0-9]*"
              autoFocus
              iconLeft={<i className="bi bi-person-vcard" aria-hidden />}
              hint="Debe contener entre 7 y 8 dígitos."
            />
          </div>
        </ModalShell>
      )}
    </div>
  );
}

export default HomePage;
